/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  IconButton,
  Divider,
} from "@mui/material";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FormInput from "./components/FormInput";
import FormSwitch from "./components/FormSwitch";
import ChipInput from "./components/ChipInput";

function EditPlanDialog({ open, onClose, plan, onSave, loading }) {
  const [formData, setFormData] = useState({
    planName: "",
    subTitle: "",
    price: 0,
    duration: "",
    features: [],
    isPopular: false,
    isPremium: false,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (plan) {
      setFormData({
        planName: plan.planName || "",
        subTitle: plan.subTitle || "",
        price: plan.price || 0,
        duration: plan.duration || "",
        features: plan.features || [],
        isPopular: plan.isPopular || false,
        isPremium: plan.isPremium || false,
      });
      setErrors({});
    }
  }, [plan]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleAddFeature = (feature) => {
    setFormData({
      ...formData,
      features: [...formData.features, feature],
    });
  };

  const handleDeleteFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.planName.trim()) {
      newErrors.planName = "Plan name is required";
    }
    if (!formData.price || formData.price < 0) {
      newErrors.price = "Price must be a positive number";
    }
    if (!formData.duration.trim()) {
      newErrors.duration = "Duration is required";
    }
    if (formData.features.length === 0) {
      newErrors.features = "At least one feature is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <ArgonTypography variant="h5" fontWeight="bold">
              Edit Plan Details
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text">
              Update plan information and features
            </ArgonTypography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Plan Name */}
          <Grid item xs={12}>
            <FormInput
              label="Plan Name"
              name="planName"
              value={formData.planName}
              onChange={handleChange}
              placeholder="Plan name"
              required
              error={!!errors.planName}
              helperText={errors.planName}
            />
          </Grid>

          {/* Sub Title */}
          <Grid item xs={12}>
            <FormInput
              label="Sub Title"
              name="subTitle"
              value={formData.subTitle}
              onChange={handleChange}
              placeholder="Plan subtitle"
            />
          </Grid>

          {/* Price */}
          <Grid item xs={12} md={6}>
            <FormInput
              label="Price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              type="number"
              placeholder="Enter price"
              required
              startIcon={<CurrencyRupeeIcon fontSize="small" />}
              error={!!errors.price}
              helperText={errors.price}
            />
          </Grid>

          {/* Duration */}
          <Grid item xs={12} md={6}>
            <FormInput
              label="Duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              placeholder="/month, /year, etc."
              required
              startIcon={<AccessTimeIcon fontSize="small" />}
              error={!!errors.duration}
              helperText={errors.duration}
            />
          </Grid>

          {/* Is Popular Switch */}
          <Grid item xs={12}>
            <FormSwitch
              label="Mark as Popular"
              name="isPopular"
              checked={formData.isPopular}
              onChange={handleChange}
              helperText="Popular plans will be highlighted to users"
            />
          </Grid>

          {/* Is Premium Switch */}
          <Grid item xs={12}>
            <FormSwitch
              label="Mark as Premium"
              name="isPremium"
              checked={formData.isPremium}
              onChange={handleChange}
              helperText="Premium plans offer advanced features"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Features */}
          <Grid item xs={12}>
            <ChipInput
              label="Plan Features"
              items={formData.features}
              onAdd={handleAddFeature}
              onDelete={handleDeleteFeature}
              placeholder="Type a feature and press Enter"
            />
            {errors.features && (
              <ArgonTypography variant="caption" color="error">
                {errors.features}
              </ArgonTypography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <ArgonButton onClick={onClose} color="secondary" variant="outlined" disabled={loading}>
          Cancel
        </ArgonButton>
        <ArgonButton onClick={handleSubmit} color="primary" disabled={loading}>
          {loading ? "Updating..." : "Update Plan"}
        </ArgonButton>
      </DialogActions>
    </Dialog>
  );
}

export default EditPlanDialog;
